import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAdminUsers, useAdminUserMutations } from '@/features/admin/hooks/useAdminUsers'
import { AdminUserRow } from '@/features/admin/components/AdminUserRow'
import { PlatformMetrics } from '@/features/admin/components/PlatformMetrics'
import { Spinner } from '@/components/ui/Spinner'

type FilterTab = 'all' | 'active' | 'suspended'

export function AdminUsersPage() {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filter, setFilter] = useState<FilterTab>('all')
  const [page, setPage] = useState(1)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isSuspended = filter === 'suspended' ? true : filter === 'active' ? false : undefined

  const { data, isLoading, isError } = useAdminUsers(debouncedSearch || undefined, isSuspended, page)
  const mutations = useAdminUserMutations()

  const isMutating =
    mutations.suspend.isPending ||
    mutations.reactivate.isPending ||
    mutations.delete.isPending

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setSearch(val)
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(val)
      setPage(1)
    }, 300)
  }

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: t('admin.users') },
    { key: 'active', label: t('admin.status.active') },
    { key: 'suspended', label: t('admin.status.suspended') },
  ]

  const totalActive = data?.data.filter((u) => !u.is_suspended && !u.deleted_at).length ?? 0

  return (
    <main id="main-content" className="min-h-screen bg-surface-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">{t('admin.title')}</h1>
            <p className="mt-0.5 text-sm text-neutral-600">
              {t('admin.active_users', { count: totalActive })}
            </p>
          </div>
        </div>

        <PlatformMetrics />

        <div className="rounded-2xl border border-surface-200 bg-white overflow-hidden">
          {/* Controls */}
          <div className="p-4 border-b border-surface-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-1" role="tablist" aria-label={t('admin.users')}>
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={filter === tab.key}
                  onClick={() => { setFilter(tab.key); setPage(1) }}
                  className={`px-3 py-1.5 min-h-[44px] text-sm rounded-md transition-colors ${
                    filter === tab.key
                      ? 'bg-mindeia-primary-light text-mindeia-primary font-medium'
                      : 'text-neutral-600 hover:bg-surface-50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <input
              type="search"
              value={search}
              onChange={handleSearchChange}
              placeholder={t('admin.search_users')}
              aria-label={t('admin.search_users')}
              className="w-full sm:w-64 px-3 py-2 text-sm border border-surface-200 rounded-md focus:outline-none focus:ring-2 focus:ring-mindeia-primary placeholder:text-neutral-400"
            />
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : isError ? (
            <div className="py-16 text-center">
              <p className="text-mindeia-error text-sm">{t('common.error')}</p>
            </div>
          ) : !data?.data.length ? (
            <div className="py-16 text-center">
              <p className="text-neutral-500 text-sm">{t('common.loading')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-surface-200 bg-surface-50">
                    <th className="py-3 px-4 text-xs font-semibold text-neutral-600 uppercase tracking-wide">{t('admin.col_name')}</th>
                    <th className="py-3 px-4 text-xs font-semibold text-neutral-600 uppercase tracking-wide">{t('admin.col_email')}</th>
                    <th className="py-3 px-4 text-xs font-semibold text-neutral-600 uppercase tracking-wide">{t('admin.col_status')}</th>
                    <th className="py-3 px-4 text-xs font-semibold text-neutral-600 uppercase tracking-wide">{t('admin.col_registered')}</th>
                    <th className="py-3 px-4 text-xs font-semibold text-neutral-600 uppercase tracking-wide">{t('admin.col_actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((user) => (
                    <AdminUserRow
                      key={user.id}
                      user={user}
                      isLoading={isMutating}
                      onSuspend={(id) => mutations.suspend.mutate(id)}
                      onReactivate={(id) => mutations.reactivate.mutate(id)}
                      onDelete={(id) => mutations.delete.mutate(id)}
                      onForceReset={(id) => mutations.forceReset.mutate(id)}
                      onExport={(id) => mutations.exportData.mutate(id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {data && data.meta.last_page > 1 && (
            <div className="px-4 py-3 border-t border-surface-200 flex items-center justify-between">
              <p className="text-sm text-neutral-600">
                Page {data.meta.current_page} / {data.meta.last_page}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  aria-label={t('common.previous')}
                  className="min-h-[44px] px-3 py-1 text-sm rounded border border-surface-200 text-neutral-700 hover:bg-surface-50 disabled:opacity-40 transition-colors"
                >
                  {t('common.previous')}
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(data.meta.last_page, p + 1))}
                  disabled={page === data.meta.last_page}
                  aria-label={t('common.next')}
                  className="min-h-[44px] px-3 py-1 text-sm rounded border border-surface-200 text-neutral-700 hover:bg-surface-50 disabled:opacity-40 transition-colors"
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  )
}
